import axios from 'axios'

const configUrl = `/keycloak_${process.env.REACT_APP_ENV_NAME}.json?date=2026`
const REDIRECT_URI = `${window.location.origin}/`
const PKCE_CODE_VERIFIER_KEY = 'pkce_code_verifier'
const POST_LOGIN_PATH_KEY = 'post_login_path'
const JWT_STORAGE_KEY = 'jwt'
const FORM_URLENCODED_HEADER = {
  'Content-Type': 'application/x-www-form-urlencoded',
}
const REFRESH_SKEW_SECONDS = 60
const MIN_REFRESH_DELAY_MS = 10000

let _config = null
let _tokens = null
let _tokenReceivedAtMs = null
let _refreshTimeoutId = null
let _onTokenCallback = null
let _isRefreshing = false
let _visibilityHandler = null
let _refreshPromise = null

export const getKeycloakConfig = async () => {
  if (_config) return _config
  const res = await fetch(configUrl)
  _config = await res.json()
  return _config
}

const getRealmUrl = config => {
  const authServerUrl = String(config['auth-server-url'] || '').replace(
    /\/+$/,
    ''
  )
  return `${authServerUrl}/realms/${config.realm}/protocol/openid-connect`
}

const toUrlEncodedBody = params => {
  const filteredParams = Object.entries(params).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value
    }
    return result
  }, {})

  return new URLSearchParams(filteredParams).toString()
}

const postUrlEncoded = async (url, params) => {
  const { data } = await axios.post(
    url,
    toUrlEncodedBody(params),
    {
      headers: FORM_URLENCODED_HEADER,
      skipAuthRefresh: true,
    }
  )
  return data
}

const notifyTokenUpdate = () => {
  if (_onTokenCallback) _onTokenCallback(_tokens.access_token)
}

const setTokens = tokenResponse => {
  if (!tokenResponse) return null

  _tokens = {
    ..._tokens,
    ...tokenResponse,
    refresh_token:
      tokenResponse.refresh_token || _tokens?.refresh_token || undefined,
  }
  _tokenReceivedAtMs = Date.now()

  return _tokens
}

const getCurrentPathWithQueryAndHash = () => {
  const params = new URLSearchParams(window.location.search)
  ;[
    'code',
    'state',
    'session_state',
    'iss',
    'error',
    'error_description',
  ].forEach(paramKey => params.delete(paramKey))

  const sanitizedQuery = params.toString()
  const search = sanitizedQuery ? `?${sanitizedQuery}` : ''
  return `${window.location.pathname}${search}${window.location.hash}`
}

const savePostLoginPath = () => {
  const currentPath = getCurrentPathWithQueryAndHash()
  if (currentPath && currentPath !== '/') {
    sessionStorage.setItem(POST_LOGIN_PATH_KEY, currentPath)
    return
  }
  sessionStorage.removeItem(POST_LOGIN_PATH_KEY)
}

const consumePostLoginPath = () => {
  const savedPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY)
  sessionStorage.removeItem(POST_LOGIN_PATH_KEY)
  if (savedPath && savedPath.startsWith('/')) return savedPath
  return ''
}

const redirectToLogin = async config => {
  savePostLoginPath()
  window.location.href = await getAuthUrl(config)
}

const generateCodeVerifier = () => {
  const array = new Uint8Array(32)
  window.crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const generateCodeChallenge = async verifier => {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await window.crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const getAuthUrl = async (config, extraParams = {}) => {
  const codeVerifier = generateCodeVerifier()
  sessionStorage.setItem(PKCE_CODE_VERIFIER_KEY, codeVerifier)

  const codeChallenge = await generateCodeChallenge(codeVerifier)

  const base = `${getRealmUrl(config)}/auth`
  const params = new URLSearchParams({
    client_id: config.resource,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: 'openid',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    ...extraParams,
  })

  return `${base}?${params.toString()}`
}

export const fetchTokenByCode = async code => {
  const config = await getKeycloakConfig()
  const tokenUrl = `${getRealmUrl(config)}/token`
  const codeVerifier = sessionStorage.getItem(PKCE_CODE_VERIFIER_KEY)

  const requestBody = {
    grant_type: 'authorization_code',
    code,
    client_id: config.resource,
    client_secret: config.credentials?.secret,
    redirect_uri: REDIRECT_URI,
    ...(codeVerifier ? { code_verifier: codeVerifier } : {}),
  }

  const data = await postUrlEncoded(tokenUrl, requestBody)

  sessionStorage.removeItem(PKCE_CODE_VERIFIER_KEY)
  return data
}

const refreshAccessToken = async () => {
  const config = await getKeycloakConfig()
  const tokenUrl = `${getRealmUrl(config)}/token`

  return postUrlEncoded(tokenUrl, {
    grant_type: 'refresh_token',
    refresh_token: _tokens.refresh_token,
    client_id: config.resource,
    client_secret: config.credentials?.secret,
  })
}

const refreshTokenWithLock = async (config, logMessage) => {
  if (!_tokens?.refresh_token) return null

  if (_refreshPromise) {
    return _refreshPromise
  }

  _isRefreshing = true
  _refreshPromise = (async () => {
    setTokens(await refreshAccessToken())
    if (logMessage) {
      console.log(logMessage)
    }
    notifyTokenUpdate()
    scheduleTokenRefresh(config)
    return _tokens?.access_token || null
  })()

  try {
    return await _refreshPromise
  } finally {
    _isRefreshing = false
    _refreshPromise = null
  }
}

const clearRefreshTimer = () => {
  if (_refreshTimeoutId) {
    clearTimeout(_refreshTimeoutId)
    _refreshTimeoutId = null
  }
}

const clearVisibilityHandler = () => {
  if (_visibilityHandler) {
    document.removeEventListener('visibilitychange', _visibilityHandler)
    _visibilityHandler = null
  }
}

const decodeJwtPayload = token => {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    )
    return JSON.parse(atob(paddedBase64))
  } catch {
    return null
  }
}

const getAccessTokenTtlMs = tokenResponse => {
  const payload = decodeJwtPayload(tokenResponse?.access_token || '')
  if (payload?.exp) {
    return Math.max(0, payload.exp * 1000 - Date.now())
  }

  if (Number.isFinite(tokenResponse?.expires_in)) {
    const issuedAtMs = _tokenReceivedAtMs || Date.now()
    return Math.max(
      0,
      issuedAtMs + tokenResponse.expires_in * 1000 - Date.now()
    )
  }

  return 60000
}

const scheduleTokenRefresh = config => {
  clearRefreshTimer()

  if (!_tokens?.refresh_token) return

  if (document.visibilityState === 'hidden') {
    return
  }

  const ttlMs = getAccessTokenTtlMs(_tokens)
  const refreshInMs = Math.max(
    MIN_REFRESH_DELAY_MS,
    ttlMs - REFRESH_SKEW_SECONDS * 1000
  )

  _refreshTimeoutId = setTimeout(async () => {
    try {
      await refreshTokenWithLock(config, 'Keycloak: Token refreshed')
    } catch (e) {
      console.error('Keycloak: Refresh failed (background timer)', e)
      clearRefreshTimer()
    }
  }, refreshInMs)
}

const registerVisibilityRefresh = config => {
  clearVisibilityHandler()

  _visibilityHandler = async () => {
    if (!_tokens?.refresh_token) return
    if (document.visibilityState === 'hidden') {
      clearRefreshTimer()
      return
    }

    const ttlMs = getAccessTokenTtlMs(_tokens)
    if (ttlMs <= REFRESH_SKEW_SECONDS * 1000 && !_isRefreshing) {
      try {
        await refreshTokenWithLock(
          config,
          'Keycloak: Token refreshed after tab active'
        )
      } catch (e) {
        console.error('Keycloak: Refresh failed after tab active', e)
        clearRefreshTimer()
      }
    }

    scheduleTokenRefresh(config)
  }

  document.addEventListener('visibilitychange', _visibilityHandler)
}

export const onTokenUpdate = callback => {
  _onTokenCallback = callback
  return () => {
    if (_onTokenCallback === callback) {
      _onTokenCallback = null
    }
  }
}

export const ensureValidToken = async (
  options = { minValiditySeconds: REFRESH_SKEW_SECONDS, forceRefresh: false }
) => {
  if (!_tokens?.access_token) return null
  if (!_tokens?.refresh_token) return _tokens.access_token

  const minValiditySeconds =
    options?.minValiditySeconds ?? REFRESH_SKEW_SECONDS
  const forceRefresh = options?.forceRefresh === true
  const ttlMs = getAccessTokenTtlMs(_tokens)

  if (!forceRefresh && ttlMs > minValiditySeconds * 1000) {
    return _tokens.access_token
  }

  const config = await getKeycloakConfig()

  try {
    return await refreshTokenWithLock(
      config,
      forceRefresh
        ? 'Keycloak: Token refreshed on 401'
        : 'Keycloak: Token refreshed before request'
    )
  } catch (error) {
    console.error('Keycloak: Refresh failed before request', error)
    clearRefreshTimer()
    return null
  }
}

export const initKeycloak = async () => {
  const config = await getKeycloakConfig()

  // Lấy code từ query string, bỏ qua #iss=... của Keycloak v26
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')

  if (code) {
    try {
      console.log('Keycloak: Exchanging code for token...')
      setTokens(await fetchTokenByCode(code))
      console.log('Keycloak: Token acquired successfully')

      // Xóa ?code=... khỏi URL
      const fallbackPath = getCurrentPathWithQueryAndHash()
      const returnPath = consumePostLoginPath() || fallbackPath || '/'
      window.history.replaceState({}, document.title, returnPath)

      // Notify App
      notifyTokenUpdate()

      // Chỉ refresh khi token sắp hết hạn để giảm request nền
      registerVisibilityRefresh(config)
      scheduleTokenRefresh(config)

      return _tokens.access_token
    } catch (error) {
      console.error('Keycloak: Code exchange failed', error)
      await redirectToLogin(config)
      return null
    }
  }

  // Chưa có code → redirect sang Keycloak login
  console.log('Keycloak: Redirecting to login...')
  await redirectToLogin(config)
  return null
}

export const logout = async () => {
  const config = await getKeycloakConfig()
  const idTokenHint = _tokens?.id_token

  clearRefreshTimer()
  clearVisibilityHandler()
  _isRefreshing = false
  _refreshPromise = null

  // Clear local app auth state before leaving the app
  _tokens = null
  _tokenReceivedAtMs = null
  localStorage.removeItem('jwt')
  sessionStorage.removeItem(JWT_STORAGE_KEY)
  sessionStorage.removeItem(PKCE_CODE_VERIFIER_KEY)
  sessionStorage.removeItem(POST_LOGIN_PATH_KEY)

  const logoutBaseUrl = `${getRealmUrl(config)}/logout`
  const logoutParams = new URLSearchParams({
    client_id: config.resource,
    post_logout_redirect_uri: REDIRECT_URI,
    // Backward compatibility for older Keycloak versions
    redirect_uri: REDIRECT_URI,
  })
  if (idTokenHint) {
    logoutParams.set('id_token_hint', idTokenHint)
  }

  window.location.href = `${logoutBaseUrl}?${logoutParams.toString()}`
}

export const getToken = () => _tokens?.access_token

// Giữ default export
export default { token: null }
